import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X } from 'lucide-react';
import { faqsAPI } from '../services/api.js';

// Inline FAQ CRUD list. Soft-delete (PUT active=false) on delete; the AI
// stops using deactivated FAQs immediately on the next call.
//
// Optional `campaignId` prop scopes the editor to one campaign: lists ONLY that
// campaign's own FAQs and stamps new ones with the campaignId. Without the prop
// it behaves exactly as before — the tenant-wide shared library.

const EMPTY = { question: '', answer: '', category: '' };

export default function FaqEditor({ campaignId = null }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({}); // id -> draft
  const [creating, setCreating] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await faqsAPI.list(campaignId ? { campaignId, only: true } : undefined);
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load FAQs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [campaignId]);

  const startEdit = (item) =>
    setEditing((e) => ({ ...e, [item.id]: { question: item.question, answer: item.answer, category: item.category || '' } }));
  const cancelEdit = (id) =>
    setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft) return;
    try {
      const r = await faqsAPI.update(id, draft);
      setItems((list) => list.map((i) => (i.id === id ? r.data : i)));
      cancelEdit(id);
    } catch (err) {
      setError(err.message || 'Save failed.');
    }
  };

  const remove = async (id) => {
    if (!confirm('Remove this FAQ from your AI?')) return;
    try {
      await faqsAPI.remove(id);
      setItems((list) => list.filter((i) => i.id !== id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  const create = async () => {
    if (!creating.question.trim() || !creating.answer.trim()) return;
    try {
      const r = await faqsAPI.create({
        question: creating.question.trim(),
        answer: creating.answer.trim(),
        category: creating.category.trim() || undefined,
        ...(campaignId ? { campaignId } : {})
      });
      setItems((list) => [r.data, ...list]);
      setCreating(EMPTY);
      setAdding(false);
    } catch (err) {
      setError(err.message || 'Create failed.');
    }
  };

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl text-stone-100">
            {campaignId ? 'Campaign FAQs' : 'Frequently asked questions'}
          </h2>
          <p className="text-sm text-stone-400 mt-1">
            {campaignId
              ? 'FAQs used ONLY on this campaign\'s calls — layered on top of the tenant-wide library.'
              : 'Each FAQ is fed into your AI\'s system prompt. Add the questions callers ask most so the AI can answer factually.'}
          </p>
        </div>
        {!adding && (
          <button type="button" className="btn-ghost" onClick={() => setAdding(true)}>
            <Plus size={14} /> Add FAQ
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {adding && (
        <div className="mb-4 rounded-xl border border-alfred-gold/30 p-4 space-y-3">
          <input
            className="field-input"
            placeholder="Question (e.g. Do you offer refunds?)"
            value={creating.question}
            onChange={(e) => setCreating({ ...creating, question: e.target.value })}
            autoFocus
          />
          <textarea
            className="field-input min-h-[90px]"
            placeholder="Answer — written like Alfred would say it on the phone"
            value={creating.answer}
            onChange={(e) => setCreating({ ...creating, answer: e.target.value })}
          />
          <input
            className="field-input"
            placeholder="Category (optional, e.g. Pricing)"
            value={creating.category}
            onChange={(e) => setCreating({ ...creating, category: e.target.value })}
          />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}>
              <Save size={14} /> Save
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setCreating(EMPTY); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">No FAQs yet. Add one to get started.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((f) => {
            const draft = editing[f.id];
            if (draft) {
              return (
                <li key={f.id} className="py-4 space-y-3">
                  <input
                    className="field-input"
                    value={draft.question}
                    onChange={(e) => setEditing((s) => ({ ...s, [f.id]: { ...s[f.id], question: e.target.value } }))}
                  />
                  <textarea
                    className="field-input min-h-[90px]"
                    value={draft.answer}
                    onChange={(e) => setEditing((s) => ({ ...s, [f.id]: { ...s[f.id], answer: e.target.value } }))}
                  />
                  <input
                    className="field-input"
                    placeholder="Category"
                    value={draft.category}
                    onChange={(e) => setEditing((s) => ({ ...s, [f.id]: { ...s[f.id], category: e.target.value } }))}
                  />
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary" onClick={() => saveEdit(f.id)}>
                      <Save size={14} /> Save
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => cancelEdit(f.id)}>
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li key={f.id} className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-stone-100 font-medium">{f.question}</span>
                      {f.category && (
                        <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">
                          {f.category}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-stone-300 leading-relaxed whitespace-pre-wrap">
                      {f.answer}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    <button type="button" className="btn-ghost" onClick={() => startEdit(f)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-red-300 hover:text-red-200"
                      onClick={() => remove(f.id)}
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
